import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, FileUp, Loader2, ShieldCheck, AlertTriangle, Clock, XCircle, RefreshCw } from 'lucide-react';
import { getToken } from '../../services/tokenService';
import { ControlesVirement } from './ControlesVirement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUTS = {
  analyse: ['Klarys vérifie le virement…', 'text-sky-700 bg-sky-50 border-sky-200', Clock],
  enregistre: ['Vérifié — paiement et reçu enregistrés', 'text-emerald-700 bg-emerald-50 border-emerald-200', CheckCircle2],
  a_verifier: ['À vérifier par l\'administration', 'text-amber-800 bg-amber-50 border-amber-200', AlertTriangle],
  doublon: ['Déjà enregistré', 'text-slate-700 bg-slate-50 border-slate-200', CheckCircle2],
  rejetee: ['Refusée', 'text-rose-700 bg-rose-50 border-rose-200', XCircle],
};

export const PreuveVirementDepot = ({ souscriptionId, onChange, obligatoire }) => {
  const [preuves, setPreuves] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [peutRelancer, setPeutRelancer] = useState(false);
  const [relance, setRelance] = useState(null);
  const input = useRef(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/preuves-virement/${souscriptionId}/liste`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setPreuves(data.preuves || []);
      setPeutRelancer(!!data.peut_relancer);
      onChange?.((data.preuves || []).length);
    } catch { /* liste indisponible */ }
  }, [souscriptionId, onChange]);

  useEffect(() => { if (souscriptionId) charger(); }, [souscriptionId, charger]);
  useEffect(() => {
    if (!preuves.some((p) => p.statut === 'analyse')) return undefined;
    const t = setTimeout(charger, 4000);
    return () => clearTimeout(t);
  }, [preuves, charger]);

  const relancer = async (id) => {
    setRelance(id);
    try {
      await axios.post(`${API}/preuves-virement/${id}/relancer`, {}, { headers });
      toast.info('Nouvelle analyse Klarys lancée');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de relancer l'analyse");
    } finally {
      setRelance(null);
    }
  };

  const deposer = async (f) => {
    if (!f) return;
    setEnvoi(true);
    const fd = new FormData();
    fd.append('fichier', f);
    try {
      await axios.post(`${API}/preuves-virement/${souscriptionId}`, fd, { headers });
      toast.success('Preuve de virement enregistrée dans la GED — vérification par Klarys en cours');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi impossible');
    } finally {
      setEnvoi(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-4" data-testid="preuve-virement-depot">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-600" />
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800">Preuve de virement {obligatoire ? <span className="text-rose-600">(obligatoire)</span> : <span className="font-normal text-slate-500">(recommandée)</span>}</p>
          <p className="text-xs text-slate-600">Téléversez l&apos;ordre de virement exécuté (PDF ou photo). Il est classé dans la GED et Klarys vérifie montant, bénéficiaire et Réf. Solidaire pour une validation immédiate.</p>
        </div>
        <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={(e) => deposer(e.target.files?.[0])} data-testid="preuve-virement-input" />
        <button type="button" disabled={envoi} onClick={() => input.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60" data-testid="preuve-virement-btn">
          {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Téléverser
        </button>
      </div>
      {preuves.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {preuves.map((p) => {
            const [lib, cls, Icon] = STATUTS[p.statut] || STATUTS.analyse;
            return (
              <React.Fragment key={p.id}>
              <li className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs ${cls}`} data-testid={`preuve-virement-${p.id}`}>
                <Icon className={`h-3.5 w-3.5 ${p.statut === 'analyse' ? 'animate-pulse' : ''}`} />
                <span className="flex-1 truncate font-medium">{p.nom}</span>
                <span data-testid={`preuve-virement-statut-${p.id}`}>{lib}</span>
                {peutRelancer && !['analyse', 'enregistre'].includes(p.statut) && (
                  <button type="button" onClick={() => relancer(p.id)} disabled={relance === p.id}
                    className="ml-1 inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
                    data-testid={`preuve-virement-relancer-${p.id}`}>
                    <RefreshCw className={`h-3 w-3 ${relance === p.id ? 'animate-spin' : ''}`} /> Relancer l&apos;analyse
                  </button>
                )}
              </li>
              <ControlesVirement preuve={p} />
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
};
